import {
    DefaultButton,
    IBreadcrumbItem,
    IconButton,
    Link,
    Panel,
    PanelType,
    Separator,
    Stack,
    Text,
    TooltipHost,
    useTheme,
} from "@fluentui/react";
import { EModelContext } from "global/Compare/ComparisonEngine";
import { Delay } from "global/Utility/TSFunctions";
import { ECommentStatus, IComment, ICommentRouteRef, ICommentRouteType } from "model/FlexibleAttributeModel";
import { useCallback } from "react";
import { CommentCard } from "./CommentCard";

export enum ECommentReviewPanelViewType {
    SidePanel,
    Embedded,
}

interface ICommentReviewPanelProps {
    comments: IComment[];
    viewType: ECommentReviewPanelViewType;
    isOpen?: boolean;
    dismissPanel?: any;
    onToggleResolved?: (comment: IComment) => void;
    onEditComment?: (comment: IComment) => void;
    onNavigate?: (rootLocation: ICommentRouteRef) => void;
    setPinned?: (pinned: boolean) => void;
}

// friendly labels for each segment of a comment's location chain, used to build the breadcrumb trail
export const ROUTE_TYPE_LABELS: Record<ICommentRouteType, string> = {
    [ICommentRouteType.root]: "Group Configuration",
    [ICommentRouteType.plan]: "Plan",
    [ICommentRouteType.benefit]: "Benefit",
    [ICommentRouteType.feesAndPremiums]: "Fees and Premiums",
    [ICommentRouteType.accountsReceivable]: "Accounts Receivable",
    [ICommentRouteType.stopLoss]: "Stop Loss",
    [ICommentRouteType.groupNumber]: "Group Number",
    [ICommentRouteType.nonStandard]: "Non-Standard",
    [ICommentRouteType.flexibleFieldSet]: "Attribute Set",
    [ICommentRouteType.tab]: "Tab",
    [ICommentRouteType.section]: "Section",
    [ICommentRouteType.configRef]: "Config",
    [ICommentRouteType.flexibleFieldRef]: "Field",
    [ICommentRouteType.flexibleFieldValueRef]: "Value",
};

// the top-level category panel (CompareListSection) that contains each entity-level location type
const ROUTE_TYPE_TO_CATEGORY_CONTEXT: Partial<Record<ICommentRouteType, string>> = {
    [ICommentRouteType.root]: EModelContext.groupConfiguration,
    [ICommentRouteType.plan]: EModelContext.plans,
    [ICommentRouteType.benefit]: EModelContext.benefits,
    [ICommentRouteType.accountsReceivable]: EModelContext.accountsReceivable,
    [ICommentRouteType.stopLoss]: EModelContext.stopLoss,
    [ICommentRouteType.feesAndPremiums]: EModelContext.feesAndPremiums,
    [ICommentRouteType.groupNumber]: EModelContext.groupNumbers,
};

// DOM attribute that identifies the container element for each location segment
const LOCATION_NODE_ATTRIBUTE: Partial<Record<ICommentRouteType, string>> = {
    [ICommentRouteType.root]: "data-category-id",
    [ICommentRouteType.plan]: "data-category-id",
    [ICommentRouteType.benefit]: "data-category-id",
    [ICommentRouteType.accountsReceivable]: "data-category-id",
    [ICommentRouteType.stopLoss]: "data-category-id",
    [ICommentRouteType.feesAndPremiums]: "data-category-id",
    [ICommentRouteType.groupNumber]: "data-category-id",
    [ICommentRouteType.nonStandard]: "data-nonstandard-id",
    [ICommentRouteType.flexibleFieldSet]: "data-attribute-set-id",
    [ICommentRouteType.tab]: "data-tab-id",
    [ICommentRouteType.section]: "data-section-id",
    [ICommentRouteType.configRef]: "data-config-attribute-id",
    [ICommentRouteType.flexibleFieldRef]: "data-config-attribute-id",
    [ICommentRouteType.flexibleFieldValueRef]: "data-value-target-id",
};

const HIGHLIGHT_COLOR = "#ffca28";
const HIGHLIGHT_ANIMATION_NAME = "commentReviewLocationHighlight";

// injects the pulse keyframes into the document once, the first time a location is flashed
let highlightKeyframesInjected = false;
const ensureHighlightKeyframes = () => {
    if (highlightKeyframesInjected)
        return;

    highlightKeyframesInjected = true;
    const style = document.createElement("style");
    style.textContent = `
        @keyframes ${HIGHLIGHT_ANIMATION_NAME} {
            0% { background-color: transparent; box-shadow: 0 0 0 0 rgba(255, 179, 0, 0); transform: scale(1); }
            10% { background-color: rgba(255, 179, 0, 1)  ; transform: scale(1.03); }
            25% { background-color: rgba(255, 179, 0, 0.5); transform: scale(1); }
            40% { background-color: rgba(255, 179, 0, 1)  ; transform: scale(1.03); }
            55% { background-color: rgba(255, 179, 0, 0.5); transform: scale(1); }
            70% { background-color: rgba(255, 179, 0, 1)  ; transform: scale(1.03); }
            100% { background-color: transparent; box-shadow: 0 0 0 0 rgba(255, 179, 0, 0); transform: scale(1); }
        }
    `;
    document.head.appendChild(style);
};

// CustomPanel sections can unmount their content while collapsed, so each ancestor must be
// expanded and given time to render before the next level down can be found in the DOM
const expandIfCollapsed = async (element: HTMLElement) => {
    const header = element.querySelector<HTMLElement>(':scope > [data-collapsed="true"]');
    if (header) {
        header.click();
        await Delay(250);
    }
};

const flashElement = (element: HTMLElement) => {
    ensureHighlightKeyframes();

    const previousAnimation = element.style.animation;
    const previousBorderRadius = element.style.borderRadius;
    const previousTransition = element.style.transition;
    const previousOutline = element.style.outline;

    element.style.transition = "";
    element.style.borderRadius = previousBorderRadius || "6px";
    element.style.outline = `2px solid ${HIGHLIGHT_COLOR}`;
    element.style.animation = `${HIGHLIGHT_ANIMATION_NAME} 3.2s ease-in-out`;

    const onAnimationEnd = () => {
        element.style.animation = previousAnimation;
        element.style.borderRadius = previousBorderRadius;
        element.style.transition = previousTransition;
        element.style.outline = previousOutline;
        element.removeEventListener("animationend", onAnimationEnd);
    };
    element.addEventListener("animationend", onAnimationEnd);
};

// walks the location chain container-by-container (category -> entity -> tab -> section -> field),
// expanding/awaiting each collapsed panel before looking for the next level, then scrolls to whichever
// segment was reached furthest - mirrors CompareQuickNavOverlay's cascaded onNavigate/ScrollToPositionCascaded
export const navigateToLocation = async (rootLocation: ICommentRouteRef, parTargetNode: ICommentRouteRef | null = null) => {

    const targetNode = parTargetNode ?? getLeafLocationNode(rootLocation);

    let lastFoundElement: HTMLElement | null = null;

    const categoryContext = ROUTE_TYPE_TO_CATEGORY_CONTEXT[rootLocation.type as ICommentRouteType];
    const categoryElement = categoryContext ? document.getElementById(categoryContext) : null;
    if (categoryElement) {
        await expandIfCollapsed(categoryElement);
        lastFoundElement = categoryElement;
    }

    let node: ICommentRouteRef | undefined = rootLocation;

    while (node) {
        if (node.id) {
            const attribute = LOCATION_NODE_ATTRIBUTE[node.type as ICommentRouteType];
            const element = attribute
                ? document.querySelector<HTMLElement>(`[${attribute}="${node.id}"]`)
                : document.getElementById(node.id);

            if (element) {
                lastFoundElement = element;
                await expandIfCollapsed(element);
            }
        }

        if (node === targetNode)
            break;

        node = node.next;
    }

    if (!lastFoundElement)
        return;

    const rect = lastFoundElement.getBoundingClientRect();
    const elementPosition = rect.top + window.pageYOffset;

    // center the element in the viewport instead of pinning it near the top
    const centeringOffset = (window.innerHeight - rect.height) / 2;

    window.scrollTo({ top: elementPosition - centeringOffset, behavior: "smooth" });
    flashElement(lastFoundElement);
};

// entity-level segments that appear right off the group configuration root
const ROUTE_TYPE_TO_ENTITY_SEGMENT: Partial<Record<ICommentRouteType, string>> = {
    [ICommentRouteType.plan]: "plan",
    [ICommentRouteType.benefit]: "benefit",
    [ICommentRouteType.feesAndPremiums]: "feesAndPremium",
    [ICommentRouteType.accountsReceivable]: "claimsAR",
    [ICommentRouteType.stopLoss]: "stopLoss",
    [ICommentRouteType.groupNumber]: "groupNumber",
};

export interface IDataEntryRoute {
    path: string;
    fieldId?: string;
}

// builds the route path used by the regular data-entry app (GroupConfiguration/Plan/Benefit/etc.), which
// addresses fields via route params (tab/section/field) instead of the Compare app's DOM attributes -
// the "2nd" tab/section/field params declared in App.tsx share the same literal path segments as the
// first-level ones, so the chain can be flattened into a single tab/section/field sequence per segment
export const buildDataEntryRoute = (rootLocation: ICommentRouteRef, basePath: string): IDataEntryRoute => {
    let path = basePath;
    let fieldId: string | undefined;

    let node: ICommentRouteRef | undefined = rootLocation;

    while (node) {
        const entitySegment = ROUTE_TYPE_TO_ENTITY_SEGMENT[node.type as ICommentRouteType];

        if (entitySegment) {
            path += `/${entitySegment}/${node.id}`;
        } else if (node.type === ICommentRouteType.tab) {
            path += `/tab/${node.id}`;
        } else if (node.type === ICommentRouteType.section) {
            path += `/section/${node.id}`;
        } else if (node.type === ICommentRouteType.nonStandard) {
            path += `/nonStandard/${node.id}`;
        } else if (node.type === ICommentRouteType.flexibleFieldSet) {
            path += `/flexibleFieldSet/${node.id}`;
        } else if (node.type === ICommentRouteType.configRef || node.type === ICommentRouteType.flexibleFieldRef) {
            path += `/field/${node.id}`;
            fieldId = node.id;
        }

        node = node.next;
    }

    return { path, fieldId };
};

export const buildBreadcrumbItems = (location: ICommentRouteRef): IBreadcrumbItem[] => {
    const items: IBreadcrumbItem[] = [];
    let node: ICommentRouteRef | undefined = location;

    while (node) {
        const label = ROUTE_TYPE_LABELS[node.type as ICommentRouteType] ?? node.type ?? "Location";
        const currentNode = node;

        items.push({
            key: `${node.type ?? "node"}-${node.id}`,
            text: (node.type === ICommentRouteType.root || !node.name) ? label : `${label}: ${node.name}`,
            onClick: () => { void navigateToLocation(location, currentNode); },
        });

        node = node.next;
    }

    // always anchor the trail at the group configuration root, even when the location chain starts lower
    if (location.type !== ICommentRouteType.root) {
        items.unshift({ key: "root", text: ROUTE_TYPE_LABELS[ICommentRouteType.root] });
    }

    return items;
};

// finds the deepest/last segment of a comment's location chain, used by the "Navigate" button
export const getLeafLocationNode = (location: ICommentRouteRef): ICommentRouteRef => {
    let node = location;
    while (node.next) {
        node = node.next;
    }
    return node;
};

// finds the closest entity-level segment with a name for use in card headers
export const getEntityLocationNode = (location: ICommentRouteRef): ICommentRouteRef | undefined => {
    let node: ICommentRouteRef | undefined = location;
    let entityNode: ICommentRouteRef | undefined;

    while (node) {
        const isEntityLevel =
            node.type === ICommentRouteType.plan ||
            node.type === ICommentRouteType.benefit ||
            node.type === ICommentRouteType.accountsReceivable ||
            node.type === ICommentRouteType.stopLoss ||
            node.type === ICommentRouteType.feesAndPremiums ||
            node.type === ICommentRouteType.groupNumber ||
            node.type === ICommentRouteType.nonStandard ||
            node.type === ICommentRouteType.flexibleFieldSet;

        if (isEntityLevel && node.name)
            entityNode = node;

        node = node.next;
    }

    return entityNode;
};

// renders each location segment on its own line, shown/hidden via the comment's "Show Route" button
export const onRenderLocationTrail = (items: IBreadcrumbItem[]) => (
    <Stack tokens={{ childrenGap: 2 }} styles={{ root: { paddingLeft: 4 } }}>
        {items.map((item) => (
            item.onClick ? (
                <Link key={item.key} onClick={item.onClick} styles={{ root: { fontSize: "12px" } }}>{item.text}</Link>
            ) : (
                <Text key={item.key} variant="small" styles={{ root: { fontSize: "12px" } }}>{item.text}</Text>
            )
        ))}
    </Stack>
);

export const ACTION_LINK_STYLES = {
    root: { fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "4px" },
};

const CommentReviewPanel: React.FC<ICommentReviewPanelProps> = (props) => {
    const theme = useTheme();

    const isPinned = props.viewType === ECommentReviewPanelViewType.Embedded;

    const unresolvedComments = props.comments.filter((comment) => comment.status !== ECommentStatus.Resolved);
    const resolvedComments = props.comments.filter((comment) => comment.status === ECommentStatus.Resolved);


    // Fluent's Panel can still shift window scroll on unmount even with focus-restore disabled; force it back
    const onDismissPanel = useCallback((ev?: React.SyntheticEvent<HTMLElement, Event> | KeyboardEvent) => {
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        props.dismissPanel?.(ev);

        // Layer/FocusTrapZone teardown can jump scroll to the top asynchronously; keep re-asserting it for a beat
        const restoreScroll = () => window.scrollTo(scrollX, scrollY);
        [0, 50, 100, 200, 350, 500].forEach((delay) => window.setTimeout(restoreScroll, delay));
    }, [props.dismissPanel]);

    const onRenderComment = (comment: IComment) => (
        <CommentCard
            key={comment.id}
            comment={comment}
            onToggleResolved={props.onToggleResolved}
            onEditComment={props.onEditComment}
            onNavigate={props.onNavigate}
        />
    );

    const onRenderHeader = () => (
        <Stack
            horizontal
            verticalAlign="center"
            horizontalAlign="space-between"
            styles={{ root: { width: "100%", paddingRight: "8px" } }}
        >
            <Text variant="xLarge" styles={{ root: { paddingLeft: '25px', paddingBottom: '8px' } }}>Revisions Requested</Text>
            <TooltipHost content={isPinned ? "Detach as side panel" : "Pin to page"}>
                {props.setPinned && (
                    <IconButton
                        onClick={() => props.setPinned?.(!isPinned)}
                        iconProps={{ iconName: isPinned ? "PinnedSolid" : "Pin" }}
                        ariaLabel={isPinned ? "Detach as side panel" : "Pin to page"}

                    />
                )}
            </TooltipHost>
        </Stack>
    );

    const onRenderBody = () => (
        <Stack tokens={{ childrenGap: "15px" }}>

            {props.comments.length === 0 && (
                <Text variant="small">No comments to review.</Text>
            )}

            {unresolvedComments.map(onRenderComment)}

            {resolvedComments.length > 0 && (
                <Stack styles={{ root: { paddingTop: '8px' } }}>
                    <Text styles={{ root: { fontWeight: 600 } }}>Resolved Comments:</Text>
                    <Separator />
                    {resolvedComments.map(onRenderComment)}
                </Stack>
            )}
        </Stack>
    );

    if (!isPinned)
        return (
            <Panel
                isOpen={props.isOpen}
                onDismiss={onDismissPanel}
                type={PanelType.medium}
                closeButtonAriaLabel="Close"
                onRenderHeader={onRenderHeader}
                isFooterAtBottom={true}
                onRenderFooterContent={() => (
                    <DefaultButton
                        text="Close"
                        onClick={() => onDismissPanel()}
                    />
                )}

                // restoring focus to the trigger button on dismiss scrolls the page back to it; disable both paths that do it
                popupProps={{ shouldRestoreFocus: false }}
                focusTrapZoneProps={{ disableRestoreFocus: true }}
            >
                <Separator />
                {onRenderBody()}
            </Panel>
        );

    if (!props.isOpen)
        return null;

    return (
        <div
            style={{
                maxHeight: 'calc(100vh - 32px)',
                overflowY: 'auto',
                border: `1px solid ${theme.semanticColors.variantBorder}`,
                borderRadius: '2px',
                backgroundColor: theme.semanticColors.bodyBackground,
            }}
        >
            <div style={{ padding: '10px', borderBottom: `1px solid ${theme.semanticColors.variantBorder}` }}>
                {onRenderHeader()}
            </div>
            <div style={{ padding: '10px' }}>
                {onRenderBody()}
            </div>
        </div>
    );
};

export default CommentReviewPanel;